/**
 * Resume state (LSC1 §12) in IndexedDB. The master key and token are wrapped with a non-extractable
 * per-device AES key so the raw IndexedDB files on disk do not reveal them. Same-origin scripts can still
 * use them — that is the accepted boundary of the model.
 */
import { concat, fromBase64Url, randomBytes, toBase64Url } from "@limeshare/lsc1";

const DB_NAME = "limeshare-resume";
const DB_VERSION = 1;
const STORE_UPLOADS = "uploads";
const STORE_KEYS = "keys";

export interface ResumeFile {
  fileId: string;
  name: string;
  size: number;
  type: string;
  lastModified: number;
  /** 32-byte container header (holds the noncePrefix) — base64url */
  header: string;
  encryptedSize: number;
  partSize: number;
  partCount: number;
  /** partNumber → ETag for parts confirmed by storage */
  parts: Record<string, string>;
}

export interface ResumeRecord {
  transferId: string;
  createdAt: number;
  draftExpiresAt: number;
  /** wrapped master key (base64url of nonce||ciphertext) */
  masterKeyWrapped: string;
  tokenWrapped: string;
  /** final share fragment (already password-wrapped if a password was chosen) */
  fragment: string;
  burn: boolean;
  protected: boolean;
  message: string | undefined;
  sender?: string | undefined;
  files: ResumeFile[];
}

function idb(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    if (typeof indexedDB === "undefined") return reject(new Error("IndexedDB unavailable"));
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE_UPLOADS)) db.createObjectStore(STORE_UPLOADS, { keyPath: "transferId" });
      if (!db.objectStoreNames.contains(STORE_KEYS)) db.createObjectStore(STORE_KEYS, { keyPath: "id" });
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB open failed"));
  });
}

function tx<T>(db: IDBDatabase, store: string, mode: IDBTransactionMode, fn: (s: IDBObjectStore) => IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    const t = db.transaction(store, mode);
    const req = fn(t.objectStore(store));
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error ?? new Error("IndexedDB request failed"));
  });
}

let deviceKeyPromise: Promise<CryptoKey> | null = null;

async function deviceKey(): Promise<CryptoKey> {
  if (!deviceKeyPromise) {
    deviceKeyPromise = (async () => {
      const db = await idb();
      const existing = await tx<{ id: string; key: CryptoKey } | undefined>(db, STORE_KEYS, "readonly", (s) => s.get("device"));
      if (existing?.key) return existing.key;
      const key = await crypto.subtle.generateKey({ name: "AES-GCM", length: 256 }, false, ["encrypt", "decrypt"]);
      await tx(db, STORE_KEYS, "readwrite", (s) => s.put({ id: "device", key }));
      return key;
    })();
  }
  return deviceKeyPromise;
}

export async function wrapWithDeviceKey(data: Uint8Array): Promise<string> {
  const key = await deviceKey();
  const nonce = randomBytes(12);
  const ct = new Uint8Array(await crypto.subtle.encrypt({ name: "AES-GCM", iv: nonce }, key, data));
  return toBase64Url(concat(nonce, ct));
}

export async function unwrapWithDeviceKey(wrapped: string): Promise<Uint8Array> {
  const key = await deviceKey();
  const bytes = fromBase64Url(wrapped);
  return new Uint8Array(await crypto.subtle.decrypt({ name: "AES-GCM", iv: bytes.subarray(0, 12) }, key, bytes.subarray(12)));
}

export async function putResume(record: ResumeRecord): Promise<void> {
  const db = await idb();
  await tx(db, STORE_UPLOADS, "readwrite", (s) => s.put(record));
}

export async function getResume(transferId: string): Promise<ResumeRecord | undefined> {
  const db = await idb();
  return tx<ResumeRecord | undefined>(db, STORE_UPLOADS, "readonly", (s) => s.get(transferId));
}

export async function deleteResume(transferId: string): Promise<void> {
  const db = await idb();
  await tx(db, STORE_UPLOADS, "readwrite", (s) => s.delete(transferId));
}

/** Pending uploads whose draft window is still open; expired ones are removed. */
export async function listResumable(): Promise<ResumeRecord[]> {
  try {
    const db = await idb();
    const all = await tx<ResumeRecord[]>(db, STORE_UPLOADS, "readonly", (s) => s.getAll());
    const now = Date.now();
    const alive: ResumeRecord[] = [];
    for (const r of all) {
      if (r.draftExpiresAt > now) alive.push(r);
      else await tx(db, STORE_UPLOADS, "readwrite", (s) => s.delete(r.transferId));
    }
    return alive.sort((a, b) => b.createdAt - a.createdAt);
  } catch {
    return [];
  }
}

export function resumeProgress(r: ResumeRecord): { done: number; total: number } {
  let done = 0;
  let total = 0;
  for (const f of r.files) {
    total += f.encryptedSize;
    const uploaded = Object.keys(f.parts).length;
    done += Math.min(f.encryptedSize, uploaded * f.partSize);
  }
  return { done, total };
}
